import mitt, { Emitter } from 'mitt'
import { DialogConfig } from '~/components/passporterUi/Dialog/dialogTypes'
import { ToastCta } from '~/components/passporterUi/Toast.vue'
import { NewFeatureDialogProps } from '~/actions/newFeature/action'
import { OnBoardingDialogProps } from '~/composables/onBoarding'

type NotificationEvent = {
	message: string
	cta?: ToastCta
}

type Events = {
	'notify-error': NotificationEvent
	'notify-success': NotificationEvent
	'open-dialog': DialogConfig
	'open-new-feature': NewFeatureDialogProps
	'open-onboarding': OnBoardingDialogProps
	'open-profile-wizard': boolean
	'profile-wizard-completed': void
	'delete-traveler': void
	'close-dialog': void
}
type ValueOf<T> = T[keyof T]

export default class EventsService {
	private static readonly bus: Emitter<Events> = mitt<Events>()

	public static emitSuccess(message: string, cta?: ToastCta): void {
		this.emit('notify-success', { message, cta })
	}

	public static emitError(message: string, cta?: ToastCta): void {
		this.emit('notify-error', { message, cta })
	}

	public static emitOpenDialog(message: DialogConfig): void {
		this.emit('open-dialog', message)
	}

	public static emitOpenNewFeature(message: NewFeatureDialogProps): void {
		this.emit('open-new-feature', message)
	}

	public static emitOpenOnBoarding(message: OnBoardingDialogProps): void {
		this.emit('open-onboarding', message)
	}

	public static emitOpenProfileWizard(simple: boolean): void {
		this.emit('open-profile-wizard', simple)
	}

	public static emitDeleteTraveler(): void {
		this.emit('delete-traveler')
	}

	public static emitCloseDialog(): void {
		this.emit('close-dialog')
	}

	public static listenSuccess(callback: (message: NotificationEvent) => void): void {
		this.listen('notify-success', callback)
	}

	public static listenError(callback: (message: NotificationEvent) => void): void {
		this.listen('notify-error', callback)
	}

	public static listenOpenDialog(callback: (message: DialogConfig) => void): void {
		this.listen('open-dialog', callback)
	}

	public static listenCloseDialog(callback: () => void): void {
		this.listen('close-dialog', callback)
	}

	public static listenOpenNewFeature(callback: (message: NewFeatureDialogProps) => void): void {
		this.listen('open-new-feature', callback)
	}

	public static listenOpenOnBoarding(callback: (message: OnBoardingDialogProps) => void): void {
		this.listen('open-onboarding', callback)
	}

	public static listenOpenProfileWizard(callback: (simple: boolean) => void): void {
		this.listen('open-profile-wizard', callback)
	}

	public static emitProfileWizardCompleted(): void {
		this.emit('profile-wizard-completed')
	}

	public static listenProfileWizardCompleted(callback: () => void): void {
		this.listen('profile-wizard-completed', callback)
	}

	public static listenDeleteTraveler(callback: () => void): void {
		this.listen('delete-traveler', callback)
	}

	public static disconnectSuccess(): void {
		this.disconnect('notify-success')
	}

	public static disconnectError(): void {
		this.disconnect('notify-error')
	}

	public static disconnectOpenDialog(): void {
		this.disconnect('open-dialog')
	}

	public static disconnectDeleteTraveler(): void {
		this.disconnect('delete-traveler')
	}

	public static emit(key: keyof Events, message?: ValueOf<Events>): void {
		this.bus.emit(key, message)
	}

	public static listen(key: keyof Events, callback: (message: any) => void): void {
		this.bus.on(key, callback)
	}

	public static disconnect(key: keyof Events): void {
		this.bus.off(key)
	}
}
